/*
 * Header account block.
 * - Guests: wraps Shopify's <shopify-account> sheet (sign-in form). Anchors it under the avatar and label,
 *   closes the mobile drawer first, and keeps Shopify's auto-focus off the close button after a tap or
 *   click (it draws a focus ring on iOS).
 * - Signed-in customers: opens the theme's own account menu dialog (Figma), anchored the same way.
 */
if (!customElements.get('header-account')) {
  const SHADOW_BUTTON_SELECTORS = ['[part~="signed-out-avatar"]', 'button[popovertarget]', 'button[aria-haspopup]', 'button'];
  // Figma: the sheet's right edge sits this far past the end of the account block (avatar + label).
  const DIALOG_OVERHANG_PX = 16;
  // Matches Shopify's breakpoint: below it the sheet is a card under the header, so only its top is anchored.
  const ANCHORED_SHEET_QUERY = '(min-width: 751px)';
  // A sheet opened within this time after a pointerdown on the account block counts as a pointer open.
  const POINTER_OPEN_WINDOW_MS = 1500;
  // Shopify focuses the close button about 10ms after opening; redirect focus for this long.
  const FOCUS_GUARD_MS = 300;

  class HeaderAccount extends HTMLElement {
    constructor() {
      super();
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onCaptureClick = this.onCaptureClick.bind(this);
      this.onSheetOpen = this.onSheetOpen.bind(this);
      this.onSheetClose = this.onSheetClose.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onDialogPointerDown = this.onDialogPointerDown.bind(this);
      this.onDialogCancel = this.onDialogCancel.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.pointerOpenUntil = 0;
      this.pressStartedOnBackdrop = false;
      this.closedWithKeyboard = false;
    }

    connectedCallback() {
      this.component = this.querySelector('shopify-account');
      this.toggle = this.querySelector('[data-account-toggle]');
      this.dialog = this.querySelector('[data-account-dialog]');
      this.closeButton = this.dialog?.querySelector('[data-account-close]');

      if (this.component) {
        this.addEventListener('pointerdown', this.onPointerDown, true);
        this.addEventListener('click', this.onCaptureClick, true);
        this.addEventListener('open', this.onSheetOpen, true);
        this.addEventListener('close', this.onSheetClose, true);
      }

      if (this.toggle && this.dialog) {
        this.mediaQuery = window.matchMedia(ANCHORED_SHEET_QUERY);
        this.toggle.addEventListener('click', this.onToggleClick);
        this.closeButton?.addEventListener('click', this.onCloseClick);
        this.dialog.addEventListener('pointerdown', this.onDialogPointerDown);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('cancel', this.onDialogCancel);
        this.dialog.addEventListener('close', this.onDialogClose);
        this.mediaQuery.addEventListener('change', this.onMediaChange);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown, true);
      this.removeEventListener('click', this.onCaptureClick, true);
      this.removeEventListener('open', this.onSheetOpen, true);
      this.removeEventListener('close', this.onSheetClose, true);
      this.toggle?.removeEventListener('click', this.onToggleClick);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.dialog?.removeEventListener('pointerdown', this.onDialogPointerDown);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('cancel', this.onDialogCancel);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.mediaQuery?.removeEventListener('change', this.onMediaChange);
    }

    /* ---------- Signed-in: theme-owned account menu ---------- */

    onToggleClick() {
      // Safeguard only: while the modal is open the toggle is inert and a click lands on the backdrop.
      if (this.dialog.open) {
        this.dialog.close();
        return;
      }
      this.closedWithKeyboard = false;
      this.closeMenuDrawer();
      this.dialog.showModal();
      this.anchor(this.dialog, () => this.dialog);
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      // Start at the greeting (tabindex="-1"), so screen readers announce it and no button shows a ring.
      this.dialog.querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
    }

    // event.detail is 0 when the button was activated with Enter or Space.
    onCloseClick(event) {
      this.closedWithKeyboard = event.detail === 0;
      this.dialog.close();
    }

    onDialogPointerDown(event) {
      this.pressStartedOnBackdrop = event.target === this.dialog;
    }

    // The content fills the dialog, so a click on the dialog element itself landed on its ::backdrop.
    // Requiring the press to start there too keeps a text selection dragged out of the card from closing it.
    onDialogClick(event) {
      if (event.target === this.dialog && this.pressStartedOnBackdrop) this.dialog.close();
      this.pressStartedOnBackdrop = false;
    }

    // Escape
    onDialogCancel() {
      this.closedWithKeyboard = true;
    }

    // The menu is anchored for one layout; close it rather than leave it misplaced after crossing 750px.
    onMediaChange() {
      if (this.dialog.open) this.dialog.close();
    }

    // Fires for the close button, a backdrop click and Escape alike.
    onDialogClose() {
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      // Safari does not focus a button on click, so the native focus restore can land on <body>.
      // After a keyboard close put focus back on the toggle; after a tap leave it, so no ring shows.
      if (this.closedWithKeyboard) this.toggle.focus({ preventScroll: true });
      this.closedWithKeyboard = false;
    }

    /* ---------- Guests: Shopify's <shopify-account> sheet ---------- */

    isComponentEvent(event) {
      return event.target === this.component;
    }

    // Selector order matters: a single comma-separated query would return the first match in tree order.
    getShadowButton() {
      const root = this.component?.shadowRoot;
      if (!root) return null;
      for (const selector of SHADOW_BUTTON_SELECTORS) {
        const match = root.querySelector(selector);
        if (match) return match;
      }
      return null;
    }

    onPointerDown() {
      this.pointerOpenUntil = performance.now() + POINTER_OPEN_WINDOW_MS;
    }

    // Capture phase: runs before the component's own click handler.
    // composedPath() also covers clicks on the slotted icon and label, whose target is the slotted span.
    onCaptureClick(event) {
      if (!event.composedPath().includes(this.component)) return;
      const button = this.getShadowButton();

      // Closing the drawer focuses the burger; move focus back so the sheet returns it to the avatar.
      if (this.closeMenuDrawer()) button?.focus({ preventScroll: true });

      // A tap on the host box around the avatar should still open the sheet.
      // Clicks coming from inside the shadow tree have a different composedPath()[0], so this never loops.
      if (event.composedPath()[0] === this.component) {
        button?.focus({ preventScroll: true });
        button?.click();
      }
    }

    onSheetOpen(event) {
      if (!this.isComponentEvent(event)) return;
      this.anchor(this.component, () => this.component.shadowRoot?.querySelector('[part~="dialog"]'));
      if (performance.now() < this.pointerOpenUntil) this.keepFocusOffCloseButton();
      this.classList.add('is-open');
    }

    onSheetClose(event) {
      if (!this.isComponentEvent(event)) return;
      this.classList.remove('is-open');
    }

    // Shopify moves focus to the sheet's close button when it opens. Safari treats that scripted focus as
    // focus-visible and draws a thick ring after a tap, so for pointer opens focus the sheet heading instead
    // (screen readers then start at the heading). Keyboard opens keep Shopify's behaviour and its ring.
    keepFocusOffCloseButton() {
      const root = this.component.shadowRoot;
      if (!root) return;
      const redirect = () => {
        const close = root.querySelector('#close-button');
        const heading = root.querySelector('#dialog-heading');
        if (!close || !heading || root.activeElement !== close) return;
        heading.tabIndex = -1;
        heading.style.outline = 'none';
        heading.focus({ preventScroll: true });
      };
      redirect();
      root.addEventListener('focusin', redirect);
      setTimeout(() => root.removeEventListener('focusin', redirect), FOCUS_GUARD_MS);
    }

    /* ---------- Shared ---------- */

    // The sheet is position: fixed in the top layer. Anchor it under this element, which wraps the avatar
    // and label: the top (and the header bottom for the mobile card) always, the right edge only where the
    // sheet is a popover. `target` receives the custom properties; `getDialog` returns the laid-out sheet.
    anchor(target, getDialog) {
      const rect = this.getBoundingClientRect();
      const headerBottom = this.closest('.shopify-section')?.getBoundingClientRect().bottom ?? rect.bottom;
      target.style.setProperty('--account-dialog-top', `${Math.max(0, Math.round(rect.bottom))}px`);
      target.style.setProperty('--account-dialog-header-bottom', `${Math.max(0, Math.round(headerBottom))}px`);
      if (!window.matchMedia(ANCHORED_SHEET_QUERY).matches) return;

      const right = Math.max(0, Math.round(document.documentElement.clientWidth - rect.right - DIALOG_OVERHANG_PX));
      target.style.setProperty('--account-dialog-right', `${right}px`);

      // Shopify's scroll lock adds scrollbar-gutter: stable, which can make the sheet's containing block
      // narrower than clientWidth. Measure the laid-out sheet once (offset* ignores the slide-in transform)
      // and correct by the difference.
      const correct = (retriesLeft) => {
        const dialog = getDialog();
        if (!dialog) return;
        if (!dialog.offsetWidth) {
          if (retriesLeft > 0) requestAnimationFrame(() => correct(retriesLeft - 1));
          return;
        }
        const targetRight = this.getBoundingClientRect().right + DIALOG_OVERHANG_PX;
        const delta = Math.round(dialog.offsetLeft + dialog.offsetWidth - targetRight);
        if (delta !== 0) target.style.setProperty('--account-dialog-right', `${Math.max(0, right + delta)}px`);
      };
      requestAnimationFrame(() => correct(1));
    }

    // MenuDrawer only closes through its summary click; losing focus is not enough (global.js).
    closeMenuDrawer() {
      const summary = this.closest('.header-apriwell')?.querySelector('header-drawer details[open] > summary');
      if (!summary) return false;
      summary.click();
      return true;
    }
  }

  customElements.define('header-account', HeaderAccount);
}
